/**
 * wsDebug - always-on, in-memory telemetry for the feathers/socket.io client.
 *
 * Design: see Internal Docs/websocket-stability-feature/INSTRUMENTATION_PLAN.md
 *
 * Guarantees:
 *   - No network. No localStorage. No persistence across refreshes.
 *   - Bounded memory: ring buffer capped at `capacity` events.
 *   - Defensive: every public method is try/catch wrapped. Nothing here should
 *     ever be allowed to break socket event handling.
 *   - Always on. No flags.
 *
 * Query from DevTools:
 *   BF.wsDebug.summary()
 *   BF.wsDebug.tail(20)
 *   BF.wsDebug.copy()
 *   BF.wsDebug.log()
 */

const CAPACITY = 2000;
const BREADCRUMB_CAPACITY = 200;

const safeNow = () => {
  try {
    return typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
  } catch (e) { return Date.now(); }
};

const safeVisibility = () => {
  try { return (typeof document !== 'undefined' && document.visibilityState) || null; }
  catch (e) { return null; }
};

const safeOnLine = () => {
  try { return typeof navigator !== 'undefined' ? !!navigator.onLine : true; }
  catch (e) { return true; }
};

const generateId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const b = new Uint8Array(16);
      crypto.getRandomValues(b);
      b[6] = (b[6] & 0x0f) | 0x40;
      b[8] = (b[8] & 0x3f) | 0x80;
      const h = Array.from(b).map(x => x.toString(16).padStart(2, '0')).join('');
      return `${h.slice(0,8)}-${h.slice(8,12)}-${h.slice(12,16)}-${h.slice(16,20)}-${h.slice(20,32)}`;
    }
  } catch (e) { /* fall through */ }
  return `fallback-${Date.now()}-${Math.floor(Math.random() * 1e9).toString(16)}`;
};

const startedAt = Date.now();
const startedAtPerf = safeNow();
const clientSessionId = generateId();

const state = {
  events: [],
  breadcrumbs: [],
  watchers: [],
  transport: null,
  lastPongLatencyMs: null,
  offlineSince: null,
};

const stats = {
  connects: 0,
  disconnects: 0,
  disconnectReasons: {},
  reconnectAttempts: 0,
  reconnects: 0,
  reconnectFailed: 0,
  connectErrors: 0,
  wsUpgradeFailures: 0,
  socketErrors: 0,
  gateResolved: 0,
  gateRejected: 0,
  gateFalsePositives: 0,
  pongSamples: 0,
  lastPongLatencyMs: null,
  maxPongLatencyMs: null,
  currentTransport: null,
  lastConnectAt: null,
  lastDisconnectAt: null,
  lastDisconnectReason: null,
  timeOfflineMsTotal: 0,
  visibilityChanges: 0,
  stateTransitions: 0,
  lastState: null,
};

const tRel = () => {
  try { return Math.round(safeNow() - startedAtPerf); }
  catch (e) { return Date.now() - startedAt; }
};

const pushEvent = (evt) => {
  try {
    state.events.push(evt);
    if (state.events.length > CAPACITY) state.events.splice(0, state.events.length - CAPACITY);
  } catch (e) { /* noop */ }
};

const formatBreadcrumb = (evt) => {
  const secs = (evt.tRel / 1000).toFixed(1);
  const prefix = `+${secs}s`.padEnd(9);
  const type = String(evt.type).padEnd(20);
  let detail = '';
  try {
    const d = evt.data || {};
    switch (evt.type) {
      case 'connect':
        detail = `id=${d.id || ''} transport=${d.transport || state.transport || ''}`;
        break;
      case 'disconnect':
        detail = `reason=${d.reason || ''} wasConnected=${d.wasConnected}`;
        break;
      case 'reconnect_attempt':
      case 'reconnecting':
        detail = `n=${d.attempt || ''}`;
        break;
      case 'reconnect':
        detail = `n=${d.attempt || ''} downMs=${d.downMs != null ? d.downMs : ''}`;
        break;
      case 'connect_error':
      case 'reconnect_error':
      case 'error':
        detail = `"${d.message || ''}"`;
        break;
      case 'upgrade':
        detail = `to=${d.transport || ''}`;
        break;
      case 'upgradeError':
        detail = `"${d.message || ''}"`;
        break;
      case 'pong':
        detail = `latencyMs=${d.latencyMs}`;
        break;
      case 'gate_resolved':
        detail = `method=${d.method || ''} waitedMs=${d.waitedMs}`;
        break;
      case 'gate_rejected':
        detail = `method=${d.method || ''} connected=${d.socketConnected} onLine=${d.onLine}`;
        break;
      case 'state':
        detail = `${d.from} -> ${d.to} (${d.reason})`;
        break;
      case 'visibility':
        detail = `state=${d.state}`;
        break;
      case 'online':
      case 'offline':
        detail = '';
        break;
      default:
        try { detail = JSON.stringify(d); } catch (_) { detail = ''; }
    }
  } catch (e) { /* leave detail empty */ }
  return `${prefix}${type}${detail}`;
};

const pushBreadcrumb = (evt) => {
  try {
    state.breadcrumbs.push(formatBreadcrumb(evt));
    if (state.breadcrumbs.length > BREADCRUMB_CAPACITY) {
      state.breadcrumbs.splice(0, state.breadcrumbs.length - BREADCRUMB_CAPACITY);
    }
  } catch (e) { /* noop */ }
};

const notifyWatchers = (evt) => {
  if (!state.watchers.length) return;
  for (const w of state.watchers) {
    try {
      if (!w.matcher || w.matcher(evt)) w.fn(evt);
    } catch (e) { /* a watcher must not break recording */ }
  }
};

const applyStatsSideEffects = (type, data) => {
  try {
    switch (type) {
      case 'connect':
        stats.connects += 1;
        stats.lastConnectAt = Date.now();
        stats.currentTransport = data && data.transport || stats.currentTransport;
        state.transport = stats.currentTransport;
        if (state.offlineSince) {
          stats.timeOfflineMsTotal += Date.now() - state.offlineSince;
          state.offlineSince = null;
        }
        break;
      case 'disconnect': {
        stats.disconnects += 1;
        stats.lastDisconnectAt = Date.now();
        const reason = (data && data.reason) || 'unknown';
        stats.lastDisconnectReason = reason;
        stats.disconnectReasons[reason] = (stats.disconnectReasons[reason] || 0) + 1;
        state.offlineSince = Date.now();
        break;
      }
      case 'connect_error':
        stats.connectErrors += 1;
        if (data && data.duringUpgrade) stats.wsUpgradeFailures += 1;
        break;
      case 'reconnect_attempt':
      case 'reconnecting':
        stats.reconnectAttempts += 1;
        break;
      case 'reconnect':
        stats.reconnects += 1;
        if (state.offlineSince) {
          stats.timeOfflineMsTotal += Date.now() - state.offlineSince;
          state.offlineSince = null;
        }
        break;
      case 'reconnect_failed':
        stats.reconnectFailed += 1;
        break;
      case 'error':
        stats.socketErrors += 1;
        break;
      case 'upgrade':
        stats.currentTransport = (data && data.transport) || stats.currentTransport;
        state.transport = stats.currentTransport;
        break;
      case 'upgradeError':
        stats.wsUpgradeFailures += 1;
        break;
      case 'pong': {
        const ms = data && typeof data.latencyMs === 'number' ? data.latencyMs : null;
        if (ms !== null) {
          stats.pongSamples += 1;
          stats.lastPongLatencyMs = ms;
          state.lastPongLatencyMs = ms;
          if (stats.maxPongLatencyMs == null || ms > stats.maxPongLatencyMs) {
            stats.maxPongLatencyMs = ms;
          }
        }
        break;
      }
      case 'gate_resolved':
        stats.gateResolved += 1;
        break;
      case 'gate_rejected':
        stats.gateRejected += 1;
        if (data && data.socketConnected === true) stats.gateFalsePositives += 1;
        break;
      case 'visibility':
        stats.visibilityChanges += 1;
        break;
      case 'state':
        stats.stateTransitions += 1;
        stats.lastState = (data && data.to) || stats.lastState;
        break;
      default:
        break;
    }
  } catch (e) { /* never let stats break recording */ }
};

const recordRaw = (type, data) => {
  try {
    const evt = {
      t: Date.now(),
      tRel: tRel(),
      type,
      data: data || null,
      connected: data && typeof data.socketConnected === 'boolean' ? data.socketConnected : null,
      transport: state.transport,
      visibility: safeVisibility(),
      onLine: safeOnLine(),
    };
    applyStatsSideEffects(type, data);
    pushEvent(evt);
    pushBreadcrumb(evt);
    notifyWatchers(evt);
    return evt;
  } catch (e) {
    return null;
  }
};

const filterEvents = (opts) => {
  let out = state.events.slice();
  try {
    if (!opts) return out;
    if (typeof opts.sinceMs === 'number') {
      const cutoff = Date.now() - opts.sinceMs;
      out = out.filter(e => e.t >= cutoff);
    }
    if (typeof opts.untilMs === 'number') {
      const cutoff = Date.now() - opts.untilMs;
      out = out.filter(e => e.t <= cutoff);
    }
    if (opts.type) {
      const types = Array.isArray(opts.type) ? opts.type : [opts.type];
      out = out.filter(e => types.indexOf(e.type) !== -1);
    }
    if (typeof opts.matcher === 'function') {
      out = out.filter(e => { try { return !!opts.matcher(e); } catch (_) { return false; } });
    }
    if (typeof opts.lastN === 'number') {
      out = out.slice(-opts.lastN);
    }
  } catch (e) { /* return whatever we got */ }
  return out;
};

const wsDebug = {
  capacity: CAPACITY,
  breadcrumbCapacity: BREADCRUMB_CAPACITY,
  startedAt,
  clientSessionId,
  stats,

  get events() { return state.events; },
  get breadcrumbs() { return state.breadcrumbs; },

  record(type, data) {
    try { return recordRaw(String(type || 'unknown'), data || {}); }
    catch (e) { return null; }
  },

  recordGate(outcome, data) {
    try {
      const type = outcome === 'resolved' ? 'gate_resolved' : 'gate_rejected';
      return recordRaw(type, data || {});
    } catch (e) { return null; }
  },

  summary() {
    try {
      return {
        clientSessionId,
        startedAt,
        uptimeMs: Date.now() - startedAt,
        stats: Object.assign({}, stats, { disconnectReasons: Object.assign({}, stats.disconnectReasons) }),
        currentTransport: state.transport,
        lastEvents: state.events.slice(-5),
      };
    } catch (e) { return { error: String(e) }; }
  },

  dump(opts) {
    try { return filterEvents(opts); }
    catch (e) { return []; }
  },

  dumpJSON(opts) {
    try {
      const payload = {
        clientSessionId,
        startedAt,
        exportedAt: Date.now(),
        url: (typeof window !== 'undefined' && window.location && window.location.href) || null,
        userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) || null,
        stats: Object.assign({}, stats, { disconnectReasons: Object.assign({}, stats.disconnectReasons) }),
        events: filterEvents(opts),
        breadcrumbs: state.breadcrumbs.slice(),
      };
      return JSON.stringify(payload, null, 2);
    } catch (e) { return JSON.stringify({ error: String(e) }); }
  },

  copy(opts) {
    try {
      const text = this.dumpJSON(opts);
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).then(
          () => { try { /* eslint-disable-next-line no-console */ console.info('[wsDebug] dump copied to clipboard'); } catch (_) {} return true; },
          (err) => { try { /* eslint-disable-next-line no-console */ console.warn('[wsDebug] clipboard write failed', err); } catch (_) {} return text; }
        );
      }
      try { /* eslint-disable-next-line no-console */ console.log(text); } catch (_) {}
      return text;
    } catch (e) { return null; }
  },

  tail(n) {
    try { return state.events.slice(-(n || 50)); }
    catch (e) { return []; }
  },

  since(ms) {
    try { return filterEvents({ sinceMs: ms }); }
    catch (e) { return []; }
  },

  byType(type) {
    try { return filterEvents({ type }); }
    catch (e) { return []; }
  },

  log() {
    try {
      const lines = state.breadcrumbs.slice();
      /* eslint-disable-next-line no-console */
      console.log(lines.join('\n'));
      return lines;
    } catch (e) { return []; }
  },

  watch(matcher, fn) {
    try {
      if (typeof matcher === 'function' && typeof fn === 'undefined') {
        fn = matcher;
        matcher = null;
      }
      if (typeof fn !== 'function') return () => {};
      const w = { matcher: matcher || null, fn };
      state.watchers.push(w);
      return () => {
        const ix = state.watchers.indexOf(w);
        if (ix !== -1) state.watchers.splice(ix, 1);
      };
    } catch (e) { return () => {}; }
  },

  clear() {
    try {
      state.events.length = 0;
      state.breadcrumbs.length = 0;
      Object.keys(stats).forEach(k => {
        if (typeof stats[k] === 'number') stats[k] = 0;
        else if (stats[k] && typeof stats[k] === 'object' && !Array.isArray(stats[k])) {
          Object.keys(stats[k]).forEach(kk => delete stats[k][kk]);
        } else if (stats[k] !== null && typeof stats[k] !== 'string') {
          stats[k] = null;
        }
      });
      stats.currentTransport = state.transport;
      stats.lastState = null;
      state.offlineSince = null;
    } catch (e) { /* noop */ }
  },
};

export default wsDebug;
export { wsDebug, clientSessionId };
